/**
 * Aba "Lotes gerados" — histórico dos lotes MDF com download do .xls
 * (formato PCP do otimizador de corte).
 */
import ConfirmDialog from "../../../components/ConfirmDialog";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiDownload, FiImage, FiRotateCcw, FiSearch, FiSend, FiSliders,
} from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  baixarXlsLote, desfazerLote, enviarLoteAoCut, listarLotes,
} from "../../../services/solicitacoes";
import { TableWrapper } from "../../../styles/shared";
import ComprovantesLoteModal from "./ComprovantesLoteModal";
import MapeamentoExportacaoPanel from "./MapeamentoExportacaoPanel";
import { dataCurta, unwrapLista } from "./helpers";
import {
  ControlesBar,
  DesktopOnly,
  ErrorMsg,
  GhostBtn,
  MobileOnly,
  SolCard,
  SolCardHead,
  SolCardMeta,
  Table,
  Td,
  Th,
  Tr,
} from "./styles";
import { Carregando, Vazio } from "../_shared/Estados";

function contagemPecas(lote) {
  return lote.pecas_count ?? lote.total_pecas ?? lote.pecas?.length ?? "—";
}

function geradoPor(lote) {
  return lote.criado_por_nome || lote.gerado_por_nome || "—";
}

const BOTAO_MENOR = { height: 26, padding: "0 10px", fontSize: 12 };

export default function LotesTab() {
  const { request, hasPermission } = useAuth();
  const toast = useToast();
  const podeConfigurar = hasPermission("solicitacao:gerar_lote");

  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState("");
  const [configAberto, setConfigAberto] = useState(false);
  // {id, numero} do lote cujos comprovantes estão abertos no modal.
  const [comprovantesDe, setComprovantesDe] = useState(null);
  const [aDesfazer, setADesfazer] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const payload = await listarLotes(request);
      setLotes(unwrapLista(payload));
    } catch (e) {
      setErro(e?.message || "Erro ao carregar os lotes.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return lotes;
    return lotes.filter((l) =>
      [l.numero, l.nome, l.num_pedido, l.cliente_nome].some((v) =>
        String(v || "").toLowerCase().includes(q),
      ),
    );
  }, [lotes, busca]);

  const baixar = (lote) => () => baixarXlsLote(lote);

  // Lote gerado que não chegou ao PharuCut (Cut fora do ar na hora): reenvia.
  const enviarAoCut = (lote) => async () => {
    await enviarLoteAoCut(request, lote.id);
    await carregar();
  };

  // Desfaz o lote (peças voltam a ficar elegíveis; o número fica livre).
  // Retornar sem chamar a API quando a gestora cancela o confirm.
  // Desfaz o lote (peças voltam a ficar elegíveis; o número fica livre).
  // Pede confirmação no diálogo do sistema (não no confirm do navegador).
  const desfazer = (lote) => async () => setADesfazer(lote);
  const desfazerDeVez = async () => {
    const lote = aDesfazer;
    const resp = await desfazerLote(request, lote.id);
    toast.success(
      `Lote ${lote.numero} desfeito — ${resp?.pecas_liberadas ?? "as"} ` +
      "peça(s) voltaram a ficar disponíveis.",
    );
    setADesfazer(null);
    await carregar();
  };

  return (
    <>
      <ControlesBar>
        <div className="search">
          <FiSearch size={15} />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por lote, pedido ou cliente…"
          />
        </div>
        {podeConfigurar && (
          <GhostBtn
            type="button"
            style={{ marginLeft: "auto" }}
            onClick={() => setConfigAberto(true)}
            title="Configurar o mapeamento de campos da planilha de exportação"
          >
            <FiSliders size={15} /> Configurar exportação
          </GhostBtn>
        )}
      </ControlesBar>

      {erro && <ErrorMsg>{erro}</ErrorMsg>}

      {loading ? (
        <Carregando />
      ) : filtrados.length === 0 ? (
        <Vazio
          titulo="Nenhum lote gerado ainda"
          descricao="Selecione assistências com peças MDF na aba Solicitações para gerar o primeiro."
        />
      ) : (
        <>
          <DesktopOnly>
            <TableWrapper>
              <Table>
                <colgroup>
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "21%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "8%" }} />
                  {/* Ações: 3 botões (~113px) + padding — 14% p/ não clipar em 1366px */}
                  <col style={{ width: "14%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <Th>Nº do lote</Th>
                    <Th>Nome</Th>
                    <Th>Pedido</Th>
                    <Th>Cliente</Th>
                    <Th>Peças</Th>
                    <Th>Gerado por</Th>
                    <Th>Data</Th>
                    <Th>Ações</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((l) => (
                    <Tr key={l.id} style={{ cursor: "default" }}>
                      <Td className="mono" title={l.lote_producao_numero ? `Lote de produção ${l.lote_producao_numero}` : l.numero}>
                        {l.numero}
                        {l.lote_producao_numero ? (
                          <div style={{ fontSize: 11, color: "var(--ink-3)" }}>prod. {l.lote_producao_numero}</div>
                        ) : null}
                        {l.falta_enviar_ao_cut ? (
                          <div style={{ fontSize: 11, color: "var(--warning-text, #b45309)" }}>
                            ⚠ não está no PharuCut
                          </div>
                        ) : null}
                      </Td>
                      <Td title={l.nome || ""}>{l.nome || "—"}</Td>
                      <Td className="mono" title={l.num_pedido || ""}>
                        {l.num_pedido || "—"}
                      </Td>
                      <Td title={l.cliente_nome || ""}>{l.cliente_nome || "—"}</Td>
                      <Td>{contagemPecas(l)}</Td>
                      <Td title={geradoPor(l)}>{geradoPor(l)}</Td>
                      <Td className="mono">{dataCurta(l.created_at)}</Td>
                      <Td>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {l.falta_enviar_ao_cut && (
                            <AsyncButton
                              variant="primary"
                              style={BOTAO_MENOR}
                              title="Enviar ao PharuCut (o lote não chegou lá)"
                              successMessage="Lote enviado ao PharuCut."
                              onClick={enviarAoCut(l)}
                            >
                              <FiSend size={13} />
                            </AsyncButton>
                          )}
                          <AsyncButton
                            variant="secondary"
                            style={BOTAO_MENOR}
                            title="Baixar planilha (XLS do otimizador)"
                            onClick={baixar(l)}
                          >
                            <FiDownload size={13} />
                          </AsyncButton>
                          <AsyncButton
                            variant="secondary"
                            style={BOTAO_MENOR}
                            title="Comprovantes das solicitações do lote (JPG/PDF/imprimir)"
                            silentSuccess
                            onClick={() =>
                              setComprovantesDe({ id: l.id, numero: l.numero })
                            }
                          >
                            <FiImage size={13} />
                          </AsyncButton>
                          <AsyncButton
                            variant="danger"
                            style={BOTAO_MENOR}
                            title="Desfazer lote (peças voltam a ficar disponíveis)"
                            silentSuccess
                            onClick={desfazer(l)}
                          >
                            <FiRotateCcw size={13} />
                          </AsyncButton>
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          </DesktopOnly>

          <MobileOnly>
            {filtrados.map((l) => (
              <SolCard key={l.id} style={{ cursor: "default" }}>
                <SolCardHead>
                  <strong>{l.numero}</strong>
                </SolCardHead>
                <SolCardMeta>
                  <span>{l.nome || "—"}</span>
                  <span>Pedido {l.num_pedido || "—"}</span>
                  <span>{contagemPecas(l)} peças</span>
                  <span>{geradoPor(l)}</span>
                  <span>{dataCurta(l.created_at)}</span>
                </SolCardMeta>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {l.falta_enviar_ao_cut && (
                    <AsyncButton
                      variant="primary"
                      style={BOTAO_MENOR}
                      successMessage="Lote enviado ao PharuCut."
                      onClick={enviarAoCut(l)}
                    >
                      <FiSend size={13} /> Enviar ao PharuCut
                    </AsyncButton>
                  )}
                  <AsyncButton
                    variant="secondary"
                    style={BOTAO_MENOR}
                    onClick={baixar(l)}
                  >
                    <FiDownload size={13} /> Baixar planilha
                  </AsyncButton>
                  <AsyncButton
                    variant="secondary"
                    style={BOTAO_MENOR}
                    silentSuccess
                    onClick={() =>
                      setComprovantesDe({ id: l.id, numero: l.numero })
                    }
                  >
                    <FiImage size={13} /> Comprovantes
                  </AsyncButton>
                  <AsyncButton
                    variant="danger"
                    style={BOTAO_MENOR}
                    silentSuccess
                    onClick={desfazer(l)}
                  >
                    <FiRotateCcw size={13} /> Desfazer
                  </AsyncButton>
                </div>
              </SolCard>
            ))}
          </MobileOnly>
        </>
      )}

      {configAberto && (
        <MapeamentoExportacaoPanel
          open
          onClose={() => setConfigAberto(false)}
        />
      )}

      {comprovantesDe && (
        <ComprovantesLoteModal
          loteId={comprovantesDe.id}
          numeroLote={comprovantesDe.numero}
          onClose={() => setComprovantesDe(null)}
        />
      )}
      <ConfirmDialog
        open={Boolean(aDesfazer)}
        titulo={aDesfazer ? `Desfazer o lote ${aDesfazer.numero}?` : ""}
        descricao={aDesfazer ? `As peças voltam a ficar disponíveis para um novo lote e o número "${aDesfazer.numero}" fica livre. O XLS já baixado não some do seu computador — descarte a planilha antiga para não cortar em dobro.` : ""}
        confirmarLabel="Desfazer lote"
        tone="danger"
        onConfirmar={desfazerDeVez}
        onCancelar={() => setADesfazer(null)}
      />
    </>
  );
}
